import {
  Color,
  DepthTexture,
  HalfFloatType,
  Mesh,
  MeshNormalMaterial,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector2,
  WebGLRenderTarget,
  type PerspectiveCamera,
  type WebGLRenderer,
} from 'three';

/**
 * Screen-space outline for the low-poly look (GDD §3, §14).
 *
 * 1. Render the scene (layers 0 + 1) into a colour target with a depth texture.
 * 2. Render layer 0 again with a normal material into a normal target.
 * 3. A full-screen pass darkens pixels where depth or normals jump.
 *
 * One pass outlines every object, which stays cheap with hundreds of ships.
 * Objects that must not get outlines (sky, glows) live on layer 1 only.
 */
export const LAYER_NO_OUTLINE = 1;

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  #include <packing>
  uniform sampler2D tColor;
  uniform sampler2D tDepth;
  uniform sampler2D tNormal;
  uniform vec2 texel;
  uniform float cameraNear;
  uniform float cameraFar;
  uniform float thickness;
  uniform vec3 outlineColor;
  varying vec2 vUv;

  float viewDepth(vec2 uv) {
    float z = texture2D(tDepth, uv).x;
    return -perspectiveDepthToViewZ(z, cameraNear, cameraFar);
  }

  vec3 normalAt(vec2 uv) {
    return texture2D(tNormal, uv).xyz * 2.0 - 1.0;
  }

  void main() {
    vec4 base = texture2D(tColor, vUv);
    vec2 dx = vec2(texel.x * thickness, 0.0);
    vec2 dy = vec2(0.0, texel.y * thickness);

    float d = viewDepth(vUv);
    float depthJump = (
      abs(viewDepth(vUv + dx) - d) + abs(viewDepth(vUv - dx) - d) +
      abs(viewDepth(vUv + dy) - d) + abs(viewDepth(vUv - dy) - d)
    ) / max(d, 1e-3);

    vec3 n = normalAt(vUv);
    float normalJump =
      (1.0 - dot(n, normalAt(vUv + dx))) + (1.0 - dot(n, normalAt(vUv - dx))) +
      (1.0 - dot(n, normalAt(vUv + dy))) + (1.0 - dot(n, normalAt(vUv - dy)));

    float edge = max(smoothstep(0.06, 0.14, depthJump), smoothstep(0.45, 0.9, normalJump));
    gl_FragColor = vec4(mix(base.rgb, outlineColor, edge * 0.92), 1.0);
    #include <colorspace_fragment>
  }
`;

export class OutlineRenderer {
  private colorTarget!: WebGLRenderTarget;
  private normalTarget!: WebGLRenderTarget;
  private readonly normalMaterial = new MeshNormalMaterial({ flatShading: true });
  private readonly quadScene = new Scene();
  private readonly quadCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly material: ShaderMaterial;

  constructor(
    private readonly renderer: WebGLRenderer,
    outlineColor = new Color('#0b0d14'),
  ) {
    this.material = new ShaderMaterial({
      vertexShader,
      fragmentShader,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        tColor: { value: null },
        tDepth: { value: null },
        tNormal: { value: null },
        texel: { value: new Vector2() },
        cameraNear: { value: 0.1 },
        cameraFar: { value: 1000 },
        thickness: { value: 1.5 },
        outlineColor: { value: outlineColor },
      },
    });
    this.quadScene.add(new Mesh(new PlaneGeometry(2, 2), this.material));
    this.setSize(renderer.domElement.width, renderer.domElement.height);
  }

  /** Size in device pixels (canvas width/height). */
  setSize(width: number, height: number): void {
    this.colorTarget?.dispose();
    this.normalTarget?.dispose();
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    this.colorTarget = new WebGLRenderTarget(w, h, {
      type: HalfFloatType,
      depthTexture: new DepthTexture(w, h),
    });
    this.normalTarget = new WebGLRenderTarget(w, h);
    this.material.uniforms.texel!.value.set(1 / w, 1 / h);
    this.material.uniforms.tColor!.value = this.colorTarget.texture;
    this.material.uniforms.tDepth!.value = this.colorTarget.depthTexture;
    this.material.uniforms.tNormal!.value = this.normalTarget.texture;
  }

  /** Outline width in device pixels; scaled with zoom by the caller. */
  setThickness(px: number): void {
    this.material.uniforms.thickness!.value = px;
  }

  render(scene: Scene, camera: PerspectiveCamera): void {
    const { renderer } = this;
    const layersMask = camera.layers.mask;

    camera.layers.enable(0);
    camera.layers.enable(LAYER_NO_OUTLINE);
    renderer.setRenderTarget(this.colorTarget);
    renderer.render(scene, camera);

    const background = scene.background;
    camera.layers.set(0);
    scene.overrideMaterial = this.normalMaterial;
    scene.background = null;
    renderer.setRenderTarget(this.normalTarget);
    renderer.setClearColor(0x8080ff, 1); // "facing the camera" normal for empty space
    renderer.clear();
    renderer.render(scene, camera);
    renderer.setClearColor(0x000000, 0);
    scene.overrideMaterial = null;
    scene.background = background;
    camera.layers.mask = layersMask;

    this.material.uniforms.cameraNear!.value = camera.near;
    this.material.uniforms.cameraFar!.value = camera.far;
    renderer.setRenderTarget(null);
    renderer.render(this.quadScene, this.quadCamera);
  }

  dispose(): void {
    this.colorTarget.dispose();
    this.normalTarget.dispose();
    this.normalMaterial.dispose();
    this.material.dispose();
  }
}
